import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import {
  FolderIcon,
  PlusIcon,
  TrashIcon,
  PencilIcon,
  ArrowDownTrayIcon,
  MagnifyingGlassIcon,
  ExclamationTriangleIcon,
  DocumentTextIcon,
  ClockIcon,
  TagIcon,
  XMarkIcon,
  EyeIcon,
  EyeSlashIcon,
} from '@heroicons/react/24/outline';
import apiClient from '../services/api';
import { ExportDropdownMenu } from '../components/ui/ExportDropdownMenu';
import { useThemeStore } from '../store/themeStore';

interface DocumentCategory {
  id: string;
  name: string;
  description: string;
  icon: string;
}

interface FinancialDocument {
  id: string;
  title: string;
  category: string;
  institution?: string;
  document_date?: string;
  expiration_date?: string;
  notes?: string;
  tags: string[];
  file_name?: string;
  file_type?: string;
  has_file: boolean;
  created_at: string;
  updated_at: string;
}

interface QuickReferenceItem {
  id: string;
  category: string;
  label: string;
  value: string;
  notes?: string;
  masked: boolean;
}

interface QuickReferenceData {
  accounts: QuickReferenceItem[];
  contacts: QuickReferenceItem[];
  dates: QuickReferenceItem[];
  emergency: QuickReferenceItem[];
}

const defaultCategories: DocumentCategory[] = [
  { id: 'tax', name: 'Tax Documents', description: 'W-2, 1099, tax returns', icon: 'receipt-percent' },
  { id: 'bank', name: 'Bank Statements', description: 'Checking, savings statements', icon: 'building-library' },
  { id: 'investment', name: 'Investment Statements', description: 'Brokerage, 401(k), IRA', icon: 'chart-bar' },
  { id: 'insurance', name: 'Insurance Policies', description: 'Life, health, auto, home', icon: 'shield-check' },
  { id: 'loan', name: 'Loan Documents', description: 'Mortgage, auto, student loans', icon: 'document-text' },
  { id: 'property', name: 'Property Documents', description: 'Deeds, titles, property tax', icon: 'home' },
  { id: 'estate', name: 'Estate Documents', description: 'Wills, trusts, POA', icon: 'document-duplicate' },
  { id: 'credit', name: 'Credit Reports', description: 'Credit reports and monitoring', icon: 'credit-card' },
  { id: 'receipts', name: 'Receipts & Warranties', description: 'Purchase receipts, warranties', icon: 'receipt-refund' },
  { id: 'other', name: 'Other Documents', description: 'Miscellaneous documents', icon: 'folder' },
];

export const FinancialDocuments: React.FC = () => {
  const { t } = useTranslation('documents');
  const theme = useThemeStore((state) => state.theme);

  // Determine if we're in dark mode for date input styling
  const isDarkMode = theme === 'dark' ||
    (theme === 'system' && typeof window !== 'undefined' && window.matchMedia('(prefers-color-scheme: dark)').matches);
  const dateInputStyle: React.CSSProperties = { colorScheme: isDarkMode ? 'dark' : 'light' };

  // State
  const [activeTab, setActiveTab] = useState<'documents' | 'quickReference'>('documents');
  const [documents, setDocuments] = useState<FinancialDocument[]>([]);
  const [categories, setCategories] = useState<DocumentCategory[]>(defaultCategories);
  const [selectedCategory, setSelectedCategory] = useState<string>('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Quick Reference state
  const [quickReference, setQuickReference] = useState<QuickReferenceData>({
    accounts: [],
    contacts: [],
    dates: [],
    emergency: [],
  });
  const [showMasked, setShowMasked] = useState<Record<string, boolean>>({});

  // Modal state
  const [showDocumentModal, setShowDocumentModal] = useState(false);
  const [showQuickRefModal, setShowQuickRefModal] = useState(false);
  const [editingDocument, setEditingDocument] = useState<FinancialDocument | null>(null);
  const [editingQuickRef, setEditingQuickRef] = useState<QuickReferenceItem | null>(null);

  // Form state
  const [documentForm, setDocumentForm] = useState({
    title: '',
    category: 'other',
    institution: '',
    document_date: '',
    expiration_date: '',
    notes: '',
    tags: '',
  });
  const [selectedFile, setSelectedFile] = useState<File | null>(null);

  const [quickRefForm, setQuickRefForm] = useState({
    category: 'accounts',
    label: '',
    value: '',
    notes: '',
    masked: false,
  });

  // Load data on mount
  useEffect(() => {
    loadDocuments();
    loadQuickReference();
    loadCategories();
  }, []);

  const loadCategories = async () => {
    try {
      const response = await apiClient.get('/financial-documents/categories');
      if (response.data.categories) {
        setCategories(response.data.categories);
      }
    } catch (err) {
      console.error('Failed to load categories:', err);
    }
  };

  const loadDocuments = async () => {
    try {
      setLoading(true);
      const response = await apiClient.get('/financial-documents/documents');
      setDocuments(response.data.documents || []);
    } catch (err) {
      console.error('Failed to load documents:', err);
      setError(t('errors.loadFailed'));
    } finally {
      setLoading(false);
    }
  };

  const loadQuickReference = async () => {
    try {
      const response = await apiClient.get('/financial-documents/quick-reference');
      setQuickReference(response.data.quick_reference || {
        accounts: [],
        contacts: [],
        dates: [],
        emergency: [],
      });
    } catch (err) {
      console.error('Failed to load quick reference:', err);
    }
  };

  const handleCreateDocument = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    try {
      const formData = new FormData();
      formData.append('title', documentForm.title);
      formData.append('category', documentForm.category);
      if (documentForm.institution) formData.append('institution', documentForm.institution);
      if (documentForm.document_date) formData.append('document_date', documentForm.document_date);
      if (documentForm.expiration_date) formData.append('expiration_date', documentForm.expiration_date);
      if (documentForm.notes) formData.append('notes', documentForm.notes);
      if (documentForm.tags) formData.append('tags', documentForm.tags);
      if (selectedFile) formData.append('file', selectedFile);

      await apiClient.post('/financial-documents/documents', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });

      setShowDocumentModal(false);
      resetDocumentForm();
      loadDocuments();
    } catch (err) {
      console.error('Failed to create document:', err);
      setError(t('errors.saveFailed'));
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateDocument = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!editingDocument) return;

    setLoading(true);
    setError(null);

    try {
      await apiClient.put(`/financial-documents/documents/${editingDocument.id}`, {
        title: documentForm.title,
        category: documentForm.category,
        institution: documentForm.institution || null,
        document_date: documentForm.document_date || null,
        expiration_date: documentForm.expiration_date || null,
        notes: documentForm.notes || null,
        tags: documentForm.tags ? documentForm.tags.split(',').map(t => t.trim()) : [],
      });

      setShowDocumentModal(false);
      setEditingDocument(null);
      resetDocumentForm();
      loadDocuments();
    } catch (err) {
      console.error('Failed to update document:', err);
      setError(t('errors.saveFailed'));
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteDocument = async (docId: string) => {
    if (!window.confirm(t('confirmDelete'))) return;

    try {
      await apiClient.delete(`/financial-documents/documents/${docId}`);
      loadDocuments();
    } catch (err) {
      console.error('Failed to delete document:', err);
      setError(t('errors.deleteFailed'));
    }
  };

  const handleDownloadDocument = async (docId: string) => {
    try {
      const response = await apiClient.get(`/financial-documents/documents/${docId}/download`);
      const { file_name, file_type, file_data } = response.data;

      const byteCharacters = atob(file_data);
      const byteNumbers = new Array(byteCharacters.length);
      for (let i = 0; i < byteCharacters.length; i++) {
        byteNumbers[i] = byteCharacters.charCodeAt(i);
      }
      const byteArray = new Uint8Array(byteNumbers);
      const blob = new Blob([byteArray], { type: file_type });

      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = file_name;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Failed to download document:', err);
      setError(t('errors.downloadFailed'));
    }
  };

  const handleViewDocument = async (docId: string) => {
    try {
      const response = await apiClient.get(`/financial-documents/documents/${docId}/download`);
      const { file_name, file_type, file_data } = response.data;

      const byteCharacters = atob(file_data);
      const byteNumbers = new Array(byteCharacters.length);
      for (let i = 0; i < byteCharacters.length; i++) {
        byteNumbers[i] = byteCharacters.charCodeAt(i);
      }
      const byteArray = new Uint8Array(byteNumbers);
      const blob = new Blob([byteArray], { type: file_type });

      const url = window.URL.createObjectURL(blob);

      // Open in new window/tab for viewing
      // PDFs and images can be viewed directly in the browser
      // For other file types, the browser will handle appropriately
      const newWindow = window.open(url, '_blank');

      // If browser blocks popup, fall back to download behavior
      if (!newWindow) {
        // Fall back to download if popup is blocked
        const link = document.createElement('a');
        link.href = url;
        link.download = file_name;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      }

      // Clean up blob URL after a delay to allow viewing
      setTimeout(() => {
        window.URL.revokeObjectURL(url);
      }, 60000); // Keep URL valid for 1 minute
    } catch (err) {
      console.error('Failed to view document:', err);
      setError(t('errors.downloadFailed'));
    }
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);
  };

  const generateExportContent = () => {
    let content = `Financial Documents Inventory\n`;
    content += `Generated: ${new Date().toLocaleString()}\n\n`;

    content += `=== SUMMARY ===\n`;
    content += `Total Documents: ${documents.length}\n`;
    content += `Categories: ${categories.length}\n\n`;

    content += `=== DOCUMENTS BY CATEGORY ===\n`;
    categories.forEach(cat => {
      const catDocs = documents.filter(d => d.category === cat.id);
      if (catDocs.length > 0) {
        content += `\n${cat.name} (${catDocs.length})\n`;
        catDocs.forEach(doc => {
          content += `  - ${doc.title}`;
          if (doc.institution) content += ` (${doc.institution})`;
          if (doc.document_date) content += ` - ${doc.document_date}`;
          content += `\n`;
        });
      }
    });

    return content;
  };

  const handleExportCSV = () => {
    const headers = ['Title', 'Category', 'Institution', 'Document Date', 'Expiration Date', 'Tags', 'Has File', 'Created'];
    const rows = documents.map(d => [
      d.title,
      categories.find(c => c.id === d.category)?.name || d.category,
      d.institution || '',
      d.document_date || '',
      d.expiration_date || '',
      d.tags.join('; '),
      d.has_file ? 'Yes' : 'No',
      new Date(d.created_at).toLocaleDateString(),
    ]);

    const csvContent = [
      headers.join(','),
      ...rows.map(row => row.map(cell => `"${cell}"`).join(',')),
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `financial_documents_${new Date().toISOString().split('T')[0]}.csv`;
    link.click();
  };

  const handleExport = async (format: 'word' | 'pdf' | 'text' | 'csv') => {
    if (format === 'csv') {
      handleExportCSV();
      return;
    }

    try {
      const content = generateExportContent();
      const response = await apiClient.post('/export/document', {
        content,
        title: 'Financial Documents Inventory',
        format,
      }, {
        responseType: 'blob',
      });

      const extension = format === 'word' ? 'docx' : format;
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `auxilio_documents_inventory_${new Date().toISOString().slice(0, 10)}.${extension}`);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (err: any) {
      setError('Failed to export document');
    }
  };

  const handlePrint = () => {
    const content = generateExportContent();
    const printWindow = window.open('', '_blank');
    if (printWindow) {
      printWindow.document.write(`
        <html>
          <head>
            <title>Financial Documents Inventory</title>
            <style>
              body { font-family: Arial, sans-serif; padding: 20px; white-space: pre-wrap; }
              h1 { color: #10b981; }
            </style>
          </head>
          <body>
            <h1>Financial Documents Inventory</h1>
            <pre>${content}</pre>
          </body>
        </html>
      `);
      printWindow.document.close();
      printWindow.print();
    }
  };

  const handleCreateQuickRef = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);

    try {
      await apiClient.post('/financial-documents/quick-reference', quickRefForm);
      setShowQuickRefModal(false);
      resetQuickRefForm();
      loadQuickReference();
    } catch (err) {
      console.error('Failed to create quick reference item:', err);
      setError(t('errors.saveFailed'));
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateQuickRef = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!editingQuickRef) return;

    setLoading(true);

    try {
      await apiClient.put(`/financial-documents/quick-reference/${editingQuickRef.id}`, quickRefForm);
      setShowQuickRefModal(false);
      setEditingQuickRef(null);
      resetQuickRefForm();
      loadQuickReference();
    } catch (err) {
      console.error('Failed to update quick reference item:', err);
      setError(t('errors.saveFailed'));
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteQuickRef = async (itemId: string) => {
    if (!window.confirm(t('confirmDeleteQuickRef'))) return;

    try {
      await apiClient.delete(`/financial-documents/quick-reference/${itemId}`);
      loadQuickReference();
    } catch (err) {
      console.error('Failed to delete quick reference item:', err);
    }
  };

  const resetDocumentForm = () => {
    setDocumentForm({
      title: '',
      category: 'other',
      institution: '',
      document_date: '',
      expiration_date: '',
      notes: '',
      tags: '',
    });
    setSelectedFile(null);
  };

  const resetQuickRefForm = () => {
    setQuickRefForm({
      category: 'accounts',
      label: '',
      value: '',
      notes: '',
      masked: false,
    });
  };

  const openEditDocument = (doc: FinancialDocument) => {
    setEditingDocument(doc);
    setDocumentForm({
      title: doc.title,
      category: doc.category,
      institution: doc.institution || '',
      document_date: doc.document_date || '',
      expiration_date: doc.expiration_date || '',
      notes: doc.notes || '',
      tags: doc.tags.join(', '),
    });
    setShowDocumentModal(true);
  };

  const openEditQuickRef = (item: QuickReferenceItem) => {
    setEditingQuickRef(item);
    setQuickRefForm({
      category: item.category,
      label: item.label,
      value: item.value,
      notes: item.notes || '',
      masked: item.masked,
    });
    setShowQuickRefModal(true);
  };

  const toggleMasked = (itemId: string) => {
    setShowMasked(prev => ({ ...prev, [itemId]: !prev[itemId] }));
  };

  const maskValue = (value: string) => {
    if (value.length <= 4) return '****';
    return '****' + value.slice(-4);
  };

  // Filter documents
  const filteredDocuments = documents.filter(doc => {
    const matchesCategory = selectedCategory === 'all' || doc.category === selectedCategory;
    const matchesSearch = !searchQuery ||
      doc.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
      doc.institution?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      doc.tags.some(tag => tag.toLowerCase().includes(searchQuery.toLowerCase()));
    return matchesCategory && matchesSearch;
  });

  const inputClass = "w-full px-4 py-2 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-emerald-500 dark:[color-scheme:dark]";
  const selectClass = "w-full px-4 py-2 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-emerald-500";

  return (
    <div className="flex flex-col h-full p-6">
      {/* Header */}
      <div className="text-center mb-6">
        <div className="flex items-center justify-center mb-4">
          <FolderIcon className="h-12 w-12 text-emerald-500" />
        </div>
        <h1 className="text-3xl font-bold text-gray-900 dark:text-gray-100">
          {t('title')}
        </h1>
        <p className="text-gray-600 dark:text-gray-400 mt-2">
          {t('subtitle')}
        </p>
      </div>

      {/* Tabs */}
      <div className="flex justify-center mb-6">
        <div className="inline-flex bg-gray-100 dark:bg-gray-800 rounded-lg p-1">
          <button
            onClick={() => setActiveTab('documents')}
            className={`px-4 py-2 rounded-lg transition-colors ${
              activeTab === 'documents'
                ? 'bg-white dark:bg-gray-700 text-emerald-600 dark:text-emerald-400 shadow'
                : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-200'
            }`}
          >
            {t('tabs.documents')}
          </button>
          <button
            onClick={() => setActiveTab('quickReference')}
            className={`px-4 py-2 rounded-lg transition-colors ${
              activeTab === 'quickReference'
                ? 'bg-white dark:bg-gray-700 text-emerald-600 dark:text-emerald-400 shadow'
                : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-200'
            }`}
          >
            {t('tabs.quickReference')}
          </button>
        </div>
      </div>

      {error && (
        <div className="mb-4 p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg text-red-700 dark:text-red-300">
          {error}
        </div>
      )}

      <div className="flex-1 max-w-7xl mx-auto w-full overflow-hidden">
        {activeTab === 'documents' ? (
          <div className="flex flex-col h-full">
            {/* Documents Header */}
            <div className="flex flex-wrap items-center justify-between gap-4 mb-4">
              <div className="flex items-center gap-4">
                <select
                  value={selectedCategory}
                  onChange={(e) => setSelectedCategory(e.target.value)}
                  className={selectClass + " w-48"}
                >
                  <option value="all">{t('allCategories')}</option>
                  {categories.map(cat => (
                    <option key={cat.id} value={cat.id}>{t(`categories.${cat.id}`, { defaultValue: cat.name })}</option>
                  ))}
                </select>
                <div className="relative">
                  <MagnifyingGlassIcon className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
                  <input
                    type="text"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    placeholder={t('searchPlaceholder')}
                    className={inputClass + " pl-10 w-64"}
                  />
                </div>
              </div>
              <div className="flex items-center gap-2">
                <ExportDropdownMenu
                  onExport={handleExport}
                  onPrint={handlePrint}
                  onExportCSV={handleExportCSV}
                  showCSV={true}
                  showPrint={true}
                />
                <button
                  onClick={() => {
                    setEditingDocument(null);
                    resetDocumentForm();
                    setShowDocumentModal(true);
                  }}
                  className="flex items-center gap-2 px-4 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700"
                >
                  <PlusIcon className="h-5 w-5" />
                  {t('addDocument')}
                </button>
              </div>
            </div>

            {/* Documents List */}
            <div className="flex-1 overflow-y-auto">
              {loading ? (
                <div className="flex items-center justify-center h-64">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-emerald-600"></div>
                </div>
              ) : filteredDocuments.length === 0 ? (
                <div className="text-center py-12 text-gray-600 dark:text-gray-300">
                  <FolderIcon className="h-16 w-16 mx-auto mb-4 opacity-60" />
                  <p>{t('noDocuments')}</p>
                  <p className="text-sm mt-2 text-gray-500 dark:text-gray-400">
                    {t('noDocumentsDescription')}
                  </p>
                </div>
              ) : (
                <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
                  {filteredDocuments.map(doc => (
                    <div
                      key={doc.id}
                      className="bg-white dark:bg-gray-800 rounded-lg shadow p-4 border border-gray-200 dark:border-gray-700"
                    >
                      <div className="flex items-start justify-between mb-2">
                        <div className="flex items-center gap-2">
                          <DocumentTextIcon className="h-5 w-5 text-emerald-500" />
                          <h3 className="font-medium text-gray-900 dark:text-gray-100">
                            {doc.title}
                          </h3>
                        </div>
                        <div className="flex items-center gap-1">
                          {doc.has_file && (
                            <button
                              onClick={() => handleViewDocument(doc.id)}
                              className="p-1 text-gray-500 hover:text-blue-600 dark:text-gray-400 dark:hover:text-blue-400"
                              title={t('view')}
                            >
                              <EyeIcon className="h-4 w-4" />
                            </button>
                          )}
                          {doc.has_file && (
                            <button
                              onClick={() => handleDownloadDocument(doc.id)}
                              className="p-1 text-gray-500 hover:text-emerald-600 dark:text-gray-400 dark:hover:text-emerald-400"
                              title={t('download')}
                            >
                              <ArrowDownTrayIcon className="h-4 w-4" />
                            </button>
                          )}
                          <button
                            onClick={() => openEditDocument(doc)}
                            className="p-1 text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
                            title={t('edit')}
                          >
                            <PencilIcon className="h-4 w-4" />
                          </button>
                          <button
                            onClick={() => handleDeleteDocument(doc.id)}
                            className="p-1 text-gray-500 hover:text-red-600 dark:text-gray-400 dark:hover:text-red-400"
                            title={t('delete')}
                          >
                            <TrashIcon className="h-4 w-4" />
                          </button>
                        </div>
                      </div>
                      <div className="text-sm text-gray-600 dark:text-gray-400 space-y-1">
                        <p className="flex items-center gap-1">
                          <span className="inline-block px-2 py-0.5 text-xs bg-emerald-100 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-300 rounded">
                            {t(`categories.${doc.category}`, { defaultValue: categories.find(c => c.id === doc.category)?.name || doc.category })}
                          </span>
                        </p>
                        {doc.institution && <p>{doc.institution}</p>}
                        {doc.document_date && (
                          <p className="flex items-center gap-1">
                            <ClockIcon className="h-4 w-4" />
                            {new Date(doc.document_date).toLocaleDateString()}
                          </p>
                        )}
                        {doc.expiration_date && (
                          <p className="flex items-center gap-1 text-amber-600 dark:text-amber-400">
                            <ExclamationTriangleIcon className="h-4 w-4" />
                            {t('expires')}: {new Date(doc.expiration_date).toLocaleDateString()}
                          </p>
                        )}
                        {doc.tags.length > 0 && (
                          <div className="flex flex-wrap gap-1 mt-2">
                            {doc.tags.map((tag, i) => (
                              <span
                                key={i}
                                className="inline-flex items-center gap-1 px-2 py-0.5 text-xs bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300 rounded"
                              >
                                <TagIcon className="h-3 w-3" />
                                {tag}
                              </span>
                            ))}
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        ) : (
          /* Quick Reference Tab */
          <div className="flex flex-col h-full">
            <div className="flex justify-end mb-4">
              <button
                onClick={() => {
                  setEditingQuickRef(null);
                  resetQuickRefForm();
                  setShowQuickRefModal(true);
                }}
                className="flex items-center gap-2 px-4 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700"
              >
                <PlusIcon className="h-5 w-5" />
                {t('addQuickRef')}
              </button>
            </div>

            <div className="flex-1 overflow-y-auto grid gap-6 md:grid-cols-2">
              {/* Accounts */}
              <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-4 border border-gray-200 dark:border-gray-700">
                <h3 className="font-medium text-gray-900 dark:text-gray-100 mb-3">{t('quickRef.accounts')}</h3>
                {quickReference.accounts.length === 0 ? (
                  <p className="text-sm text-gray-500 dark:text-gray-400">{t('quickRef.noItems')}</p>
                ) : (
                  <div className="space-y-2">
                    {quickReference.accounts.map(item => (
                      <div key={item.id} className="flex items-center justify-between p-2 bg-gray-50 dark:bg-gray-700 rounded">
                        <div>
                          <p className="text-sm font-medium text-gray-900 dark:text-gray-100">{item.label}</p>
                          <p className="text-sm text-gray-600 dark:text-gray-400">
                            {item.masked && !showMasked[item.id] ? maskValue(item.value) : item.value}
                          </p>
                        </div>
                        <div className="flex items-center gap-1">
                          {item.masked && (
                            <button onClick={() => toggleMasked(item.id)} className="p-1 text-gray-500 hover:text-gray-700 dark:text-gray-400">
                              {showMasked[item.id] ? <EyeSlashIcon className="h-4 w-4" /> : <EyeIcon className="h-4 w-4" />}
                            </button>
                          )}
                          <button onClick={() => openEditQuickRef(item)} className="p-1 text-gray-500 hover:text-gray-700 dark:text-gray-400">
                            <PencilIcon className="h-4 w-4" />
                          </button>
                          <button onClick={() => handleDeleteQuickRef(item.id)} className="p-1 text-gray-500 hover:text-red-600 dark:text-gray-400">
                            <TrashIcon className="h-4 w-4" />
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Contacts */}
              <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-4 border border-gray-200 dark:border-gray-700">
                <h3 className="font-medium text-gray-900 dark:text-gray-100 mb-3">{t('quickRef.contacts')}</h3>
                {quickReference.contacts.length === 0 ? (
                  <p className="text-sm text-gray-500 dark:text-gray-400">{t('quickRef.noItems')}</p>
                ) : (
                  <div className="space-y-2">
                    {quickReference.contacts.map(item => (
                      <div key={item.id} className="flex items-center justify-between p-2 bg-gray-50 dark:bg-gray-700 rounded">
                        <div>
                          <p className="text-sm font-medium text-gray-900 dark:text-gray-100">{item.label}</p>
                          <p className="text-sm text-gray-600 dark:text-gray-400">{item.value}</p>
                        </div>
                        <div className="flex items-center gap-1">
                          <button onClick={() => openEditQuickRef(item)} className="p-1 text-gray-500 hover:text-gray-700 dark:text-gray-400">
                            <PencilIcon className="h-4 w-4" />
                          </button>
                          <button onClick={() => handleDeleteQuickRef(item.id)} className="p-1 text-gray-500 hover:text-red-600 dark:text-gray-400">
                            <TrashIcon className="h-4 w-4" />
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Important Dates */}
              <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-4 border border-gray-200 dark:border-gray-700">
                <h3 className="font-medium text-gray-900 dark:text-gray-100 mb-3">{t('quickRef.dates')}</h3>
                {quickReference.dates.length === 0 ? (
                  <p className="text-sm text-gray-500 dark:text-gray-400">{t('quickRef.noItems')}</p>
                ) : (
                  <div className="space-y-2">
                    {quickReference.dates.map(item => (
                      <div key={item.id} className="flex items-center justify-between p-2 bg-gray-50 dark:bg-gray-700 rounded">
                        <div>
                          <p className="text-sm font-medium text-gray-900 dark:text-gray-100">{item.label}</p>
                          <p className="text-sm text-gray-600 dark:text-gray-400">{item.value}</p>
                        </div>
                        <div className="flex items-center gap-1">
                          <button onClick={() => openEditQuickRef(item)} className="p-1 text-gray-500 hover:text-gray-700 dark:text-gray-400">
                            <PencilIcon className="h-4 w-4" />
                          </button>
                          <button onClick={() => handleDeleteQuickRef(item.id)} className="p-1 text-gray-500 hover:text-red-600 dark:text-gray-400">
                            <TrashIcon className="h-4 w-4" />
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Emergency Info */}
              <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-4 border border-gray-200 dark:border-gray-700">
                <h3 className="font-medium text-gray-900 dark:text-gray-100 mb-3">{t('quickRef.emergency')}</h3>
                {quickReference.emergency.length === 0 ? (
                  <p className="text-sm text-gray-500 dark:text-gray-400">{t('quickRef.noItems')}</p>
                ) : (
                  <div className="space-y-2">
                    {quickReference.emergency.map(item => (
                      <div key={item.id} className="flex items-center justify-between p-2 bg-gray-50 dark:bg-gray-700 rounded">
                        <div>
                          <p className="text-sm font-medium text-gray-900 dark:text-gray-100">{item.label}</p>
                          <p className="text-sm text-gray-600 dark:text-gray-400">{item.value}</p>
                        </div>
                        <div className="flex items-center gap-1">
                          <button onClick={() => openEditQuickRef(item)} className="p-1 text-gray-500 hover:text-gray-700 dark:text-gray-400">
                            <PencilIcon className="h-4 w-4" />
                          </button>
                          <button onClick={() => handleDeleteQuickRef(item.id)} className="p-1 text-gray-500 hover:text-red-600 dark:text-gray-400">
                            <TrashIcon className="h-4 w-4" />
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Document Modal */}
      {showDocumentModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between p-4 border-b border-gray-200 dark:border-gray-700">
              <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100">
                {editingDocument ? t('editDocument') : t('addDocument')}
              </h3>
              <button
                onClick={() => {
                  setShowDocumentModal(false);
                  setEditingDocument(null);
                  resetDocumentForm();
                }}
                className="text-gray-500 hover:text-gray-700 dark:text-gray-400"
              >
                <XMarkIcon className="h-6 w-6" />
              </button>
            </div>
            <form onSubmit={editingDocument ? handleUpdateDocument : handleCreateDocument} className="p-4 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">{t('form.title')}</label>
                <input
                  type="text"
                  value={documentForm.title}
                  onChange={(e) => setDocumentForm({ ...documentForm, title: e.target.value })}
                  className={inputClass}
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">{t('form.category')}</label>
                <select
                  value={documentForm.category}
                  onChange={(e) => setDocumentForm({ ...documentForm, category: e.target.value })}
                  className={selectClass}
                >
                  {categories.map(cat => (
                    <option key={cat.id} value={cat.id}>{t(`categories.${cat.id}`, { defaultValue: cat.name })}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">{t('form.institution')}</label>
                <input
                  type="text"
                  value={documentForm.institution}
                  onChange={(e) => setDocumentForm({ ...documentForm, institution: e.target.value })}
                  className={inputClass}
                  placeholder={t('form.institutionPlaceholder')}
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">{t('form.documentDate')}</label>
                  <input
                    type="date"
                    value={documentForm.document_date}
                    onChange={(e) => setDocumentForm({ ...documentForm, document_date: e.target.value })}
                    className={inputClass}
                    style={dateInputStyle}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">{t('form.expirationDate')}</label>
                  <input
                    type="date"
                    value={documentForm.expiration_date}
                    onChange={(e) => setDocumentForm({ ...documentForm, expiration_date: e.target.value })}
                    className={inputClass}
                    style={dateInputStyle}
                  />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">{t('form.tags')}</label>
                <input
                  type="text"
                  value={documentForm.tags}
                  onChange={(e) => setDocumentForm({ ...documentForm, tags: e.target.value })}
                  className={inputClass}
                  placeholder={t('form.tagsPlaceholder')}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">{t('form.notes')}</label>
                <textarea
                  value={documentForm.notes}
                  onChange={(e) => setDocumentForm({ ...documentForm, notes: e.target.value })}
                  className={inputClass}
                  rows={3}
                />
              </div>
              {!editingDocument && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">{t('form.file')}</label>
                  <input
                    type="file"
                    onChange={(e) => setSelectedFile(e.target.files?.[0] || null)}
                    className="w-full text-gray-900 dark:text-gray-100"
                  />
                </div>
              )}
              <div className="flex justify-end gap-2 pt-4">
                <button
                  type="button"
                  onClick={() => {
                    setShowDocumentModal(false);
                    setEditingDocument(null);
                    resetDocumentForm();
                  }}
                  className="px-4 py-2 text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-700 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-600"
                >
                  {t('cancel')}
                </button>
                <button
                  type="submit"
                  disabled={loading}
                  className="px-4 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 disabled:bg-gray-400"
                >
                  {loading ? t('saving') : t('save')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Quick Reference Modal */}
      {showQuickRefModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl w-full max-w-md">
            <div className="flex items-center justify-between p-4 border-b border-gray-200 dark:border-gray-700">
              <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100">
                {editingQuickRef ? t('editQuickRef') : t('addQuickRef')}
              </h3>
              <button
                onClick={() => {
                  setShowQuickRefModal(false);
                  setEditingQuickRef(null);
                  resetQuickRefForm();
                }}
                className="text-gray-500 hover:text-gray-700 dark:text-gray-400"
              >
                <XMarkIcon className="h-6 w-6" />
              </button>
            </div>
            <form onSubmit={editingQuickRef ? handleUpdateQuickRef : handleCreateQuickRef} className="p-4 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">{t('quickRef.type')}</label>
                <select
                  value={quickRefForm.category}
                  onChange={(e) => setQuickRefForm({ ...quickRefForm, category: e.target.value })}
                  className={selectClass}
                >
                  <option value="accounts">{t('quickRef.accounts')}</option>
                  <option value="contacts">{t('quickRef.contacts')}</option>
                  <option value="dates">{t('quickRef.dates')}</option>
                  <option value="emergency">{t('quickRef.emergency')}</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">{t('quickRef.label')}</label>
                <input
                  type="text"
                  value={quickRefForm.label}
                  onChange={(e) => setQuickRefForm({ ...quickRefForm, label: e.target.value })}
                  className={inputClass}
                  placeholder={t('quickRef.labelPlaceholder')}
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">{t('quickRef.value')}</label>
                <input
                  type="text"
                  value={quickRefForm.value}
                  onChange={(e) => setQuickRefForm({ ...quickRefForm, value: e.target.value })}
                  className={inputClass}
                  placeholder={t('quickRef.valuePlaceholder')}
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">{t('quickRef.notes')}</label>
                <input
                  type="text"
                  value={quickRefForm.notes}
                  onChange={(e) => setQuickRefForm({ ...quickRefForm, notes: e.target.value })}
                  className={inputClass}
                />
              </div>
              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  id="masked"
                  checked={quickRefForm.masked}
                  onChange={(e) => setQuickRefForm({ ...quickRefForm, masked: e.target.checked })}
                  className="h-4 w-4 text-emerald-600 rounded"
                />
                <label htmlFor="masked" className="text-sm text-gray-700 dark:text-gray-300">{t('quickRef.maskValue')}</label>
              </div>
              <div className="flex justify-end gap-2 pt-4">
                <button
                  type="button"
                  onClick={() => {
                    setShowQuickRefModal(false);
                    setEditingQuickRef(null);
                    resetQuickRefForm();
                  }}
                  className="px-4 py-2 text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-700 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-600"
                >
                  {t('cancel')}
                </button>
                <button
                  type="submit"
                  disabled={loading}
                  className="px-4 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 disabled:bg-gray-400"
                >
                  {loading ? t('saving') : t('save')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Disclaimer */}
      <div className="mt-6 p-4 bg-emerald-50 dark:bg-emerald-900/20 border border-emerald-200 dark:border-emerald-800 rounded-lg max-w-7xl mx-auto w-full">
        <p className="text-sm text-emerald-800 dark:text-emerald-200 text-center">
          <strong>{t('disclaimer.title')}:</strong> {t('disclaimer.text')}
        </p>
      </div>
    </div>
  );
};

export default FinancialDocuments;
